import { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { lockScroll, releaseScroll } from '../utils/scrollLock';
import { fetchStoreMetaobjects, getDistance } from '../utils/store-locator';
import { themeI18n } from '../utils/i18n_helper';
import { StoreLocation } from './StoreLocation.js';
import { multilingual_url } from '../utils/multilingual-url.js';
import { ButtonContent } from './ButtonContent.js';
import IconPin from './IconPin.js';
import IconCaret from './IconCaret.js';
import IconClose from './IconClose.js';

const cachedStoreLocations = JSON.parse(localStorage.getItem('bentley_store_locations'));
const myStoreDataInitial = JSON.parse(localStorage.getItem('bentley_my_store')); // The users selected store
const userLocation = JSON.parse(localStorage.getItem('bentley_user_location'));

export const StoreLocatorDrawer = ({ inventoryData, innerDrawer }) => {
  const [storeLocations, setStoreLocations] = useState(cachedStoreLocations);
  const [userAddress, setUserAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [userLatLng, setUserLatLng] = useState({ lat: userLocation?.lat, lng: userLocation?.lng });
  const [myStoreData, setMyStoreData] = useState(myStoreDataInitial);
  const [myStoreDataTemp, setMyStoreDataTemp] = useState(myStoreDataInitial); // Used for displaying the selected store as first store only when reopening the drawer
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [showOnlyInStock, setShowOnlyInStock] = useState(false);

  const geocoding = useMapsLibrary('geocoding');

  const storesWrapperRef = useRef(null);
  const drawerDetails = useRef(null);

  const fetchStoreLocations = async () => {
    const stores = await fetchStoreMetaobjects();

    // if store locations are already set, make sure to not overwrite old stores (to keep distance data)
    if (storeLocations) {
      const storeUpdates = stores.map((store) => {
        const oldStore = storeLocations.find((store2) => store2.id === store.id);

        if (oldStore) {
          return {...oldStore, ...store};
        } else {
          if (userLatLng.lat && userLatLng.lng) {
            const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
            return {...store, distance};
          } else {
            return store;
          }
        }
      });

      setStoreLocations(storeUpdates.sort((a, b) => a.distance - b.distance));
      localStorage.setItem('bentley_store_locations', JSON.stringify(storeUpdates));
    } else {
      setStoreLocations(stores);
      localStorage.setItem('bentley_store_locations', JSON.stringify(stores));
    }

    setLoading(false);
  };

  const geocodeAddress = (event) => {
    event.preventDefault();
    setLoading(true);

    const geocoder = new geocoding.Geocoder();

    geocoder.geocode({ address: userAddress }, (results, status) => {
      if (status === 'OK') {
        setUserLatLng({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });

        setUserAddress('');

        if (window.toast) {
          window.toast.success({
            message: themeI18n('store_locator.address_success')
          });
        }
      } else {
        if (window.toast) {
          window.toast.error({
            message: themeI18n('store_locator.address_error')
          });
        }
      }
    });

    setLoading(false);
  }

  const sortStoresByDistance = () => {
    const updatedStores = storeLocations.map(store => {
      const distance = getDistance(userLatLng.lat, parseFloat(store.latitude), userLatLng.lng,  parseFloat(store.longitude));
      return {
        ...store,
        distance,
      };
    });

    setStoreLocations(updatedStores.sort((a, b) => a.distance - b.distance));
    // update cache
    localStorage.setItem('bentley_store_locations', JSON.stringify(updatedStores));
  }

  const drawerToggle = () => {
    if (drawerDetails.current.hasAttribute('open')) {
      drawerDetails.current.setAttribute('closing', '');

      if (!innerDrawer) {
        releaseScroll();
      }

      setTimeout(() => {
        drawerDetails.current.removeAttribute('open');
        drawerDetails.current.removeAttribute('closing');
        drawerDetails.current.setAttribute('closed', '');
      }, 500);
    } else {
      drawerDetails.current.removeAttribute('closed');
      drawerDetails.current.setAttribute('open', '');
      lockScroll();

      // Fetch locations if opening drawer for the first time
      if (!drawerOpened) {
        fetchStoreLocations();
        setDrawerOpened(true);

        // if user location is not set, request it
        if (!userLatLng.lat && !userLatLng.lng && 'geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition((position) => {
            setUserLatLng({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          });
        }
      }

      if (myStoreData) {
        setMyStoreDataTemp(myStoreData);
      }
    }
  }

  useEffect(() => {
    document.addEventListener('myStoreUpdated', () => {
      const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

      setMyStoreData(newStoreData);
    });

    return () => {
      document.removeEventListener('myStoreUpdated', () => {
        const newStoreData = JSON.parse(localStorage.getItem('bentley_my_store'));

        setMyStoreData(newStoreData);
      });
    }
  }, []);

  useEffect(() => {
    if (storeLocations && userLatLng.lat && userLatLng.lng) {
      sortStoresByDistance();
      localStorage.setItem('bentley_user_location', JSON.stringify(userLatLng));
    }
  }, [userLatLng]);

  return (
    <div className='store-locator-drawer'>
      <div className='store-locator-drawer__toggle text-small' onClick={drawerToggle}>
        {inventoryData ? (
          <a href="#" className="inventory-status__link link">
            {themeI18n('products.inventory.check_other_stores')}
          </a>
        ): (
          <>
            <IconPin className='store-locator-drawer__toggle-icon' />
            {myStoreData ? (
              <div className='store-locator-drawer__toggle-store-name'>
                {myStoreData.name}
                <IconCaret className='store-locator-drawer__toggle-caret' />
              </div>
            ) : themeI18n('store_locator.select_my_store')}
          </>
        )}
      </div>

      <div className='drawer'>
        <div className='drawer__details' ref={drawerDetails}>
          <div  className={innerDrawer ? 'drawer__inner drawer__inner--right': 'drawer__inner drawer__inner--left'} >
            <div className='drawer__inner-background' onClick={drawerToggle}/>

            <div className='drawer__inner-content-wrapper color-main'>
              <div className="store-locator-drawer__header">
                <div className="store-locator-drawer__header-close" role="button" onClick={drawerToggle} aria-label={themeI18n('accessibility.close')}>
                  <IconClose />
                </div>


                <div className="store-locator-drawer__header-title h4">
                  {themeI18n('store_locator.select_my_store')}

                  <a className='text-small link' href={multilingual_url("/pages/store-locator")}>{themeI18n('store_locator.see_all_stores')}</a>
                </div>
              </div>

              <div className="drawer__inner-content">
                <form className='store-locator-drawer__address-form' onSubmit={geocodeAddress}>
                  <label htmlFor='address'>{themeI18n('store_locator.address_label')}</label>

                  <div className='store-locator-drawer__address-form-input-wrapper'>
                    <input className='store-locator-drawer__address-form-input' id='address' name='address' type='text' placeholder={themeI18n('store_locator.address_placeholder')} value={userAddress} onChange={(event)=> setUserAddress(event.target.value)} />
                    <button className='store-locator-drawer__address-form-submit button' type='submit'>
                      {themeI18n('store_locator.address_submit')}
                    </button>
                  </div>

                  {inventoryData ? (
                    <div className='store-locator-drawer__show-only-in-stock-wrapper'>
                      <label htmlFor='show-only-in-stock'>{themeI18n('store_locator.show_only_in_stock')}</label>
                      <input type='checkbox' id='show-only-in-stock' name='show-only-in-stock' checked={showOnlyInStock} onChange={() => setShowOnlyInStock(!showOnlyInStock)} />
                    </div>
                  ) : null}
                </form>

                <div className={loading ? 'store-locator-drawer__stores loading' : 'store-locator-drawer__stores'} ref={storesWrapperRef}>
                  {storeLocations ? (
                    storeLocations.map((store) => {
                      let storeInventory = inventoryData?.location_inventories.find((location) => location.location.name.includes(store.store_number));

                      if (showOnlyInStock && storeInventory?.quantity <= 0) return null;

                      if (store.id != myStoreDataTemp?.id) {
                        return <StoreLocation key={store.name} store={store} storeInventory={storeInventory} />
                      } else {
                        return <StoreLocation key={store.name} store={store} first={true} storeInventory={storeInventory} />
                      }
                  })) : null }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
